// © 2026 Camp Promethean, LLC. All Rights Reserved.
// Browser handoff only. The server validates codes and owns durable lead credit.
const STORAGE_KEY = 'spark_promo_code';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
export interface SparkAttribution { promoCode: string; capturedAt: string }
let memory: SparkAttribution[] = [];

function valid(value: unknown): value is SparkAttribution {
  if (!value || typeof value !== 'object') return false;
  const v = value as SparkAttribution;
  const age = Date.now() - Date.parse(v.capturedAt);
  return typeof v.promoCode === 'string' && /^[A-Z0-9_-]{1,64}$/.test(v.promoCode)
    && Number.isFinite(age) && age >= 0 && age < MAX_AGE_MS;
}
function read(): SparkAttribution[] {
  if (typeof window === 'undefined') return [];
  for (const storage of ['localStorage', 'sessionStorage'] as const) {
    try {
      const raw = JSON.parse(window[storage].getItem(STORAGE_KEY) || 'null');
      const values = (Array.isArray(raw) ? raw : [raw]).filter(valid).slice(0, 5);
      if (values.length) return values;
    } catch { /* Private/in-app browsers can deny storage. Keep the page usable. */ }
  }
  return memory.filter(valid);
}
export function captureSparkCode(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const code = url.searchParams.get('spark')?.trim().toUpperCase();
  memory = read();
  if (code && /^[A-Z0-9_-]{1,64}$/.test(code) && !memory.some(v => v.promoCode === code)) {
    memory = [...memory, { promoCode: code, capturedAt: new Date().toISOString() }].slice(0, 5);
  }
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memory)); } catch { /* memory fallback */ }
  if (url.searchParams.has('spark')) {
    url.searchParams.delete('spark');
    try { window.history.replaceState(window.history.state, '', url.toString()); } catch { /* optional cleanup */ }
  }
}
export function getSparkCandidates(): string[] { return read().map(v => v.promoCode); }
export function getSparkAttribution(): SparkAttribution | null { return read()[0] ?? null; }
export function getSparkPromoCode(): string | null { return getSparkAttribution()?.promoCode ?? null; }
export function clearSparkAttribution(): void {
  memory = [];
  if (typeof window === 'undefined') return;
  for (const storage of ['localStorage', 'sessionStorage'] as const) {
    try { window[storage].removeItem(STORAGE_KEY); } catch { /* optional storage */ }
  }
}
export function hasSparkAttribution(): boolean { return getSparkAttribution() !== null; }
